import { useSharedPdfViewerGate } from '@liscio/common';
import {
  Box,
  Dialog,
  DocumentPreviewDialog,
  Icon,
  Link,
  Stack,
  Typography,
} from '@liscio/ui';

import { FirmPdfPreview } from 'components/DocumentPreview/FirmPdfPreview';
import PSPDFKitViewer from 'components/PSPDFKit/PSPDFKitViewer';
import { defaultToolbarItems } from 'components/PSPDFKit/toolbarItems';

type RequestPdfDialogProps = {
  open: boolean;
  url: string;
  title: string;
  /** Shown above the viewer with an "Open in new tab" fallback link. */
  notice?: string;
  onClose: () => void;
};

/**
 * Dialog to display a PDF viewer.
 */
export const RequestPdfDialog = ({
  open,
  url,
  title,
  notice,
  onClose,
}: RequestPdfDialogProps) => {
  const sharedViewer = useSharedPdfViewerGate(open ? 'open' : null);
  if (sharedViewer) {
    return (
      <DocumentPreviewDialog open={open} onClose={onClose} title={title}>
        {notice && <PdfNotice notice={notice} url={url} shrinkable={false} />}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <FirmPdfPreview
            url={url}
            title={/\.pdf$/i.test(title) ? title : `${title || 'Request'}.pdf`}
            height="100%"
          />
        </Box>
      </DocumentPreviewDialog>
    );
  }
  return (
    <Dialog
      open={open}
      fullScreen
      onClose={onClose}
      title={title}
      iconClose
      PaperProps={{
        sx: {
          width: '98vw',
          maxHeight: '98vh',
        },
      }}
    >
      {notice && <PdfNotice notice={notice} url={url} />}
      <Box height="100%" width="100%" position="relative">
        <PSPDFKitViewer toolbarItems={defaultToolbarItems} url={url} />
      </Box>
    </Dialog>
  );
};

function PdfNotice({
  notice,
  url,
  shrinkable = true,
}: {
  notice: string;
  url: string;
  /** The shared viewer's dialog is a flex column that the notice must not shrink in. */
  shrinkable?: boolean;
}) {
  return (
    <Stack
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        ...(shrinkable ? { py: 1 } : { p: 1, flexShrink: 0 }),
      }}
    >
      {/* body2 is bold in some themes; keep the notice regular on every screen. */}
      <Typography variant="body2" sx={{ fontWeight: 'fontWeightRegular' }}>
        {notice}
      </Typography>
      <Link
        component="button"
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
      >
        <Icon icon="download" fontSize="small" /> Open in new tab
      </Link>
    </Stack>
  );
}
